import { makeAutoObservable, runInAction } from 'mobx';

import type { AiChatSourceRef } from '@/api/ai-chat/types';
import {
  isSessionMissingError,
  loadListedSessionDetail,
} from '@/page/ai-app/ai-chat/service/sessionLoader';
import type { ChatApiStrategyKey } from '@/page/ai-app/ai-chat/service/types';
import { DEFAULT_CHAT_API_STRATEGY_KEY } from '@/page/ai-app/ai-chat/service/types';
import type {
  SessionMessage,
  SessionRecordRefresh,
  SessionStatus,
} from '@/page/ai-app/ai-chat/types';
import {
  hasConversationMessages,
  withSessionRoundNumber,
} from '@/page/ai-app/ai-chat/util/message';
import type { ChatSession } from '@/page/ai-app/ai-chat/util/session';
import {
  createSession,
  detailPlaceholder,
} from '@/page/ai-app/ai-chat/util/session';
import {
  type CustomerSessionPreview,
  isShareSessionId,
} from '@/page/ai-app/ai-chat/util/sessionPath';
import STORE from '@/store';

export interface KefuVerifyTarget {
  appId: string;
  data: string;
  messageId: string;
  prompts: string[];
  /** 一次打开。静默重算 data 不换它，面板不重挂。 */
  openId: string;
  /** 点「新对话」后，欢迎语和第一条用户消息带 newSession。 */
  newSession?: boolean;
}

type SourceAttachOptions = {
  sourceRef?: AiChatSourceRef;
  sourcePreview?: CustomerSessionPreview;
};

const toSessionTitle = (content: string) =>
  content.length > 18 ? `${content.slice(0, 18)}...` : content;

const isDetailPlaceholder = (session?: ChatSession) => {
  const messages = session?.messages || [];
  return messages.length === 1 && messages[0]?.type === 'loading';
};

const hasSavedMessages = (session?: ChatSession) =>
  hasConversationMessages(session?.messages);

const clearSourceAttach = (session: ChatSession) => {
  if (!session.pendingSourceRef && !session.pendingSourcePreview) return;
  session.pendingSourceRef = undefined;
  session.pendingSourcePreview = undefined;
};

/** 详情与空白复用的竞态字段。非观察，组件不能读。 */
const race = {
  detailToken: 0,
  detailInflightId: '',
  pendingBlankId: '',
};

/**
 * 已经发生过的助手会话 id 在 sessionId。
 * 空白新任务不写。发送，或打开历史会话时写入。
 * 「新任务」是否另开一轮看 sessionId，不看地址栏。
 * 会话文档（sessions / activeSessionId 等）也在这里。
 */
class AgentChatStore {
  /** 已经发生的会话。空白新任务为空。 */
  public sessionId = '';
  /** 当前绑定的产品 lv3Id。 */
  public boundAppId = '';
  public sessions: ChatSession[] = [];
  public activeSessionId = '';
  /** idle 可发送。pending 是正在生成或续传。拉历史详情时保持 idle。 */
  public sessionStatus: SessionStatus = 'idle';
  public detailLoading = false;
  /** 失效分享 token。非空时页面弹窗，关掉再去空白新任务。 */
  public expiredShareSessionId = '';
  public sessionRecordRefresh: SessionRecordRefresh = {
    epoch: 0,
    sessionId: '',
  };
  /** 右侧并排的客服验证面板。空表示收起。 */
  public kefuVerify: KefuVerifyTarget | null = null;

  public constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** 当前会话。先按 activeSessionId 找，找不到用列表第一条。 */
  public get activeSession(): ChatSession | undefined {
    return (
      this.sessions.find((session) => session.id === this.activeSessionId) ||
      this.sessions[0]
    );
  }

  /** 当前会话的消息。没有会话时为空数组。 */
  public get activeMessages(): SessionMessage[] {
    return this.activeSession?.messages || [];
  }

  /** 当前会话的接口策略。会话未指定时用 optimization。 */
  public get apiStrategyKey(): ChatApiStrategyKey {
    return this.activeSession?.apiStrategyKey || DEFAULT_CHAT_API_STRATEGY_KEY;
  }

  /** 输入框上方的客服会话引用卡片。没有引用时为空。 */
  public get pendingSourcePreview(): CustomerSessionPreview | undefined {
    return this.activeSession?.pendingSourcePreview;
  }

  /** 续联首帧前的加载占位。当前会话 resumeLoading 为真时为 true。 */
  public get resumeLoading(): boolean {
    return !!this.activeSession?.resumeLoading;
  }

  public sessionById(sessionId: string): ChatSession | undefined {
    return this.sessions.find((session) => session.id === sessionId);
  }

  public messageById(
    sessionId: string,
    messageId: string
  ): SessionMessage | undefined {
    return this.sessionById(sessionId)?.messages?.find(
      (message) => message.id === messageId
    );
  }

  /**
   * ready 且有 lv3Id 时绑定。已是这个 app 则返回。
   * 否则整份丢掉再写一条本地空白。
   */
  public bindApp(appId: string) {
    if (!appId) return;
    if (this.boundAppId === appId) return;
    this.reset();
    const session = createSession(appId);
    this.boundAppId = appId;
    this.sessions = [session];
    this.activeSessionId = session.id;
    this.sessionStatus = 'idle';
  }

  /** 只清仍属于这次 app 的那份。 */
  public releaseApp(appId: string) {
    if (!appId || this.boundAppId !== appId) return;
    this.reset();
  }

  public reset() {
    this.sessionId = '';
    this.boundAppId = '';
    this.sessions = [];
    this.activeSessionId = '';
    this.sessionStatus = 'idle';
    this.detailLoading = false;
    this.expiredShareSessionId = '';
    this.sessionRecordRefresh = { epoch: 0, sessionId: '' };
    this.kefuVerify = null;
    race.detailToken = 0;
    race.detailInflightId = '';
    race.pendingBlankId = '';
  }

  public setSessionId(sessionId: string) {
    if (!sessionId || this.sessionId === sessionId) return;
    this.sessionId = sessionId;
  }

  public openKefuVerify(target: KefuVerifyTarget) {
    this.kefuVerify = target;
  }

  /** 只换凭证。openId 不动，已打开的面板接着用新 data。 */
  public replaceKefuVerifyCredential(appId: string, data: string) {
    if (!this.kefuVerify || !appId || !data) return;
    this.kefuVerify.appId = appId;
    this.kefuVerify.data = data;
  }

  public closeKefuVerify() {
    if (!this.kefuVerify) return;
    this.kefuVerify = null;
  }

  public clearSessionId() {
    this.kefuVerify = null;
    if (!this.sessionId) return;
    this.sessionId = '';
  }

  public setSessionStatus(
    value: SessionStatus | ((current: SessionStatus) => SessionStatus)
  ) {
    this.sessionStatus =
      typeof value === 'function' ? value(this.sessionStatus) : value;
  }

  public setDetailLoading(value: boolean | ((current: boolean) => boolean)) {
    this.detailLoading =
      typeof value === 'function' ? value(this.detailLoading) : value;
  }

  /** 改正文就地改那一条，不换 sessions，也不换消息对象。 */
  public patchMessage(
    sessionId: string,
    messageId: string,
    updater: (message: SessionMessage) => SessionMessage
  ) {
    const session = this.sessionById(sessionId);
    const messages = session?.messages;
    if (!session || !messages) return;
    const current = messages.find((message) => message.id === messageId);
    if (!current) return;
    const next = updater(current);
    if (next !== current) {
      const nextKeys = new Set(Object.keys(next));
      Object.keys(current).forEach((key) => {
        if (!nextKeys.has(key)) {
          delete (current as unknown as Record<string, unknown>)[key];
        }
      });
      Object.assign(current, next);
    }
    session.updatedAt = new Date().toISOString();
  }

  public appendMessage(sessionId: string, message: SessionMessage) {
    const session = this.sessionById(sessionId);
    if (!session) return;
    if (!session.messages) session.messages = [];
    const nextMessage = withSessionRoundNumber(message, session.messages);
    session.messages.push(nextMessage);
    session.updatedAt = new Date().toISOString();
  }

  public appendLastSessionContent(message: SessionMessage) {
    const sessionId = this.activeSessionId;
    const session = this.sessionById(sessionId);
    if (!session) return;
    if (!session.messages) session.messages = [];
    const nextMessage = withSessionRoundNumber(message, session.messages);
    session.appId = session.appId || this.boundAppId;
    session.apiStrategyKey =
      session.apiStrategyKey || DEFAULT_CHAT_API_STRATEGY_KEY;
    if (nextMessage.type === 'user' && session.title === '新对话') {
      session.title = toSessionTitle(nextMessage.content);
    }
    session.updatedAt = new Date().toISOString();
    if (nextMessage.type === 'user') {
      session.lastMessage = nextMessage.content;
    }
    session.messages.push(nextMessage);
  }

  /**
   * 切到一条空白对话，不改 URL。
   * 未传策略时按新任务用 optimization。只复用同策略的空「新对话」，不改另一条策略的空白会话。
   * 只有传入 options 时才改 pendingSourceRef（可显式传 undefined 清空）。
   */
  public showBlankChat(
    apiStrategyKey?: ChatApiStrategyKey,
    options?: SourceAttachOptions
  ) {
    if (!this.boundAppId) return;
    const strategy = apiStrategyKey || DEFAULT_CHAT_API_STRATEGY_KEY;
    const touchSource = !!options;
    const nextSourceRef = options?.sourceRef;
    const nextSourcePreview = options?.sourcePreview;
    this.detailLoading = false;
    if (race.pendingBlankId) {
      const pending = this.sessionById(race.pendingBlankId);
      if (pending && hasSavedMessages(pending)) race.pendingBlankId = '';
    }
    const isStrategyBlank = (session?: ChatSession) => {
      if (
        !session ||
        hasSavedMessages(session) ||
        isDetailPlaceholder(session)
      ) {
        return false;
      }
      return (
        (session.apiStrategyKey || DEFAULT_CHAT_API_STRATEGY_KEY) === strategy
      );
    };
    const settleBlank = (sessionId: string) => {
      const item = this.sessionById(sessionId);
      if (!item) return;
      if (!item.apiStrategyKey) item.apiStrategyKey = strategy;
      if (touchSource) {
        item.pendingSourceRef = nextSourceRef;
        item.pendingSourcePreview = nextSourcePreview;
      }
    };
    const useBlank = (sessionId: string) => {
      settleBlank(sessionId);
      this.activeSessionId = sessionId;
      this.sessionStatus = 'idle';
    };
    const active = this.activeSession;
    if (active && isStrategyBlank(active)) {
      useBlank(active.id);
      return;
    }
    const reusable = this.sessions.find(
      (item) => item.title === '新对话' && isStrategyBlank(item)
    );
    if (reusable) {
      useBlank(reusable.id);
      return;
    }
    const pending = this.sessionById(race.pendingBlankId);
    if (pending && isStrategyBlank(pending)) {
      useBlank(pending.id);
      return;
    }
    const session: ChatSession = {
      ...createSession(this.boundAppId, strategy),
      ...(touchSource
        ? {
            pendingSourceRef: nextSourceRef,
            pendingSourcePreview: nextSourcePreview,
          }
        : {}),
    };
    race.pendingBlankId = session.id;
    if (!this.sessions.some((item) => item.id === session.id)) {
      this.sessions.unshift(session);
    }
    this.activeSessionId = session.id;
    this.sessionStatus = 'idle';
  }

  public takePendingSourceRef(sessionId: string): AiChatSourceRef | undefined {
    if (!sessionId) return undefined;
    const session = this.sessionById(sessionId);
    const sourceRef = session?.pendingSourceRef;
    if (!sourceRef && !session?.pendingSourcePreview) return undefined;
    if (session) clearSourceAttach(session);
    return sourceRef;
  }

  public clearPendingSourceRef(sessionId: string) {
    if (!sessionId) return;
    const session = this.sessionById(sessionId);
    if (session) clearSourceAttach(session);
  }

  /** 服务端 session_id 和本地不一致时换掉。换成了返回 true。 */
  public adoptSessionId(fromId: string, toId: string): boolean {
    if (!fromId || !toId || fromId === toId) return false;
    if (!this.sessions.some((item) => item.id === fromId)) return false;
    if (this.sessions.some((item) => item.id === toId)) return false;
    const session = this.sessionById(fromId);
    if (!session) return false;
    session.id = toId;
    if (this.activeSessionId === fromId) this.activeSessionId = toId;
    if (race.pendingBlankId === fromId) race.pendingBlankId = toId;
    if (this.sessionId === fromId) this.sessionId = toId;
    return true;
  }

  /** 空串表示这一轮已经结束或用户停止。 */
  public setLiveTurn(sessionId: string, turnId: string) {
    const session = this.sessionById(sessionId);
    const nextTurnId = turnId || undefined;
    if (!session || session.liveTurnId === nextTurnId) return;
    session.liveTurnId = nextTurnId;
  }

  public markSessionRecordsStale(sessionId: string) {
    if (!sessionId) return;
    this.sessionRecordRefresh = {
      epoch: this.sessionRecordRefresh.epoch + 1,
      sessionId,
    };
  }

  public setResumeLoading(sessionId: string, value: boolean) {
    const session = this.sessionById(sessionId);
    if (!session || session.resumeLoading === value) return;
    session.resumeLoading = value;
  }

  public changeApiStrategy(key: ChatApiStrategyKey) {
    const session = this.sessionById(this.activeSessionId);
    if (!session || (session.messages || []).length > 0) return;
    session.apiStrategyKey = key;
  }

  public selectSession(sessionId: string) {
    if (this.activeSessionId !== sessionId) this.kefuVerify = null;
    const target = this.sessionById(sessionId);
    this.setResumeLoading(sessionId, Boolean(target?.liveTurnId));
    this.activeSessionId = sessionId;
    this.sessionStatus = 'idle';
  }

  /** 关掉失效提示：丢掉这条 share 会话，切到空白新任务。不改地址。 */
  public leaveExpiredShare() {
    const id = this.expiredShareSessionId;
    if (!id) return;
    this.expiredShareSessionId = '';
    this.removeListedSession(id);
    if (this.activeSessionId === id) this.activeSessionId = '';
    if (this.sessionId === id) this.clearSessionId();
    this.showBlankChat(undefined, {
      sourceRef: undefined,
      sourcePreview: undefined,
    });
  }

  /**
   * URL 切到一条记录：每次都整屏骨架 + 重新拉详情，按 last_status 决定是否续传。
   * 拉详情期间 sessionStatus 保持 idle，发送按钮置灰。
   * 详情拿到后若最新一轮仍在 streaming（要重连），写成 pending 并保持骨架，等重连首帧再揭；
   * 否则直接结束 loading。
   * 分享 token 详情为 session not found 时只记 expiredShareSessionId，由页面弹窗后再离开。
   */
  public openListedSession(
    session: Pick<ChatSession, 'id' | 'title' | 'createdAt' | 'updatedAt'>
  ) {
    if (!this.boundAppId) return;
    if (
      this.expiredShareSessionId &&
      this.expiredShareSessionId !== session.id
    ) {
      const stale = this.expiredShareSessionId;
      this.expiredShareSessionId = '';
      this.removeListedSession(stale);
      if (this.sessionId === stale) this.clearSessionId();
    }
    if (this.activeSessionId !== session.id) this.kefuVerify = null;
    this.setSessionId(session.id);
    const found = this.sessionById(session.id);
    if (isDetailPlaceholder(found) && race.detailInflightId === session.id) {
      this.detailLoading = true;
      this.activeSessionId = session.id;
      this.sessionStatus = 'idle';
      return;
    }

    this.detailLoading = true;
    const placeholder = detailPlaceholder(session.id);
    const existing = found;
    const strategyBeforeOpen = existing?.apiStrategyKey;
    if (existing) {
      existing.messages = [placeholder];
      existing.liveTurnId = undefined;
      existing.apiStrategyKey = 'optimization';
      if (!existing.appId) existing.appId = this.boundAppId;
    } else {
      this.sessions.unshift({
        id: session.id,
        appId: this.boundAppId,
        title: session.title,
        createdAt: session.createdAt,
        updatedAt: session.updatedAt,
        apiStrategyKey: 'optimization',
        messages: [placeholder],
        liveTurnId: undefined,
      });
    }
    this.activeSessionId = session.id;
    this.sessionStatus = 'idle';

    const token = race.detailToken + 1;
    race.detailToken = token;
    race.detailInflightId = session.id;
    const appId = this.boundAppId;
    let resumeTurnId = '';
    void loadListedSessionDetail(
      appId,
      session.id,
      STORE.user || STORE.userInfo?.user_info.user_id || ''
    )
      .then((detail) => {
        if (token !== race.detailToken) return;
        resumeTurnId = detail.resumeTurnId;
        runInAction(() => {
          if (this.expiredShareSessionId === session.id) {
            this.expiredShareSessionId = '';
          }
          const item = this.sessionById(session.id);
          if (!item) return;
          item.title = detail.title || item.title;
          item.messages = detail.messages;
          item.lastStatus = detail.lastStatus;
          item.liveTurnId = resumeTurnId || undefined;
          item.apiStrategyKey = detail.strategyKey || 'optimization';
          if (!resumeTurnId) this.detailLoading = false;
        });
      })
      .catch((error) => {
        if (token !== race.detailToken) return;
        resumeTurnId = '';
        if (isShareSessionId(session.id) && isSessionMissingError(error)) {
          runInAction(() => {
            this.expiredShareSessionId = session.id;
            this.sessionStatus = 'idle';
            this.detailLoading = false;
            const item = this.sessionById(session.id);
            if (item && strategyBeforeOpen) {
              item.apiStrategyKey = strategyBeforeOpen;
            }
            if (item && !hasSavedMessages(item)) item.messages = [];
          });
          return;
        }
        runInAction(() => {
          this.sessionStatus = 'error';
          this.detailLoading = false;
          const item = this.sessionById(session.id);
          if (item && strategyBeforeOpen) {
            item.apiStrategyKey = strategyBeforeOpen;
          }
          if (item && !hasSavedMessages(item)) item.messages = [];
        });
        console.error('[ai-chat resume] failed to load session detail', error);
      })
      .finally(() => {
        if (token !== race.detailToken) return;
        runInAction(() => {
          if (race.detailInflightId === session.id) {
            race.detailInflightId = '';
          }
          if (resumeTurnId) this.sessionStatus = 'pending';
          else if (this.sessionStatus === 'pending')
            this.sessionStatus = 'idle';
        });
      });
  }

  public renameListedSession(sessionId: string, title: string) {
    const nextTitle = title.trim();
    if (!sessionId || !nextTitle) return;
    const session = this.sessionById(sessionId);
    if (!session || session.title === nextTitle) return;
    session.title = nextTitle;
  }

  /** 删除成功后从本地会话去掉。当前会话被删时返回 true。 */
  public removeListedSession(sessionId: string): boolean {
    if (!sessionId) return false;
    const index = this.sessions.findIndex((item) => item.id === sessionId);
    if (index < 0) return false;
    this.sessions.splice(index, 1);
    if (race.pendingBlankId === sessionId) race.pendingBlankId = '';
    return this.activeSessionId === sessionId;
  }
}

const agentChatStore = new AgentChatStore();

export default agentChatStore;
